import AbcIcon from '@mui/icons-material/Abc';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import {
  Box,
  Button,
  Divider,
  Stack,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, Extension, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useMeetingContext } from '../../../contexts/meeting/meetingContext';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import { IMeetingNote } from '../../../interfaces/MeetingNotes';
import meetingService from '../../../services/meetingService';

interface NotesFormProps {
  note?: IMeetingNote;
  onClickCancel: () => void;
  onSubmit: () => void;
}

function NotesForm({
  note,
  onClickCancel,
  onSubmit,
}: Readonly<NotesFormProps>) {
  const intl = useIntl();
  const [formattingEnabled, setFormattingEnabled] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [expand, setExpand] = useState(false);
  const { meetingDetails } = useMeetingContext();
  const { createToastNotification } = useToastNotificationContext();

  const formik = useFormik({
    initialValues: {
      id: note?.id ?? '',
      notes: note?.description ?? '',
    },
    onSubmit: async (values, { resetForm }) => {
      if (meetingDetails.current) {
        if (values.id) {
          await meetingService.updateMeetingNote(values.id, values.notes);
          // reset edit mode
          onClickCancel();
        } else {
          await meetingService.createMeetingNote(
            meetingDetails.current.meeting_id,
            values.notes,
          );
        }

        onSubmit();
        resetForm();
      } else {
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Something went wrong.' }),
          'error',
        );
      }
    },
    enableReinitialize: true,
  });

  const enableFormattingShortcuts = Extension.create({
    addKeyboardShortcuts() {
      return {
        'Mod-b': () => {
          setFormattingEnabled(true);
          return false;
        },
        'Mod-i': () => {
          setFormattingEnabled(true);
          return false;
        },
        'Mod-u': () => {
          setFormattingEnabled(true);
          return false;
        },
      };
    },
  });

  const extensions = [
    StarterKit,
    Underline,
    enableFormattingShortcuts,
    Placeholder.configure({
      placeholder: intl.formatMessage({
        defaultMessage: 'Type a note.',
      }),
    }),
  ];

  const toolTipMessages = {
    formatting: intl.formatMessage({
      defaultMessage: 'Enable/Disable text formatting',
    }),
    bold: intl.formatMessage({ defaultMessage: 'Bold (Ctrl/Cmd + B)' }),
    underline: intl.formatMessage({
      defaultMessage: 'Underline (Ctrl/Cmd + U)',
    }),
    italic: intl.formatMessage({ defaultMessage: 'Italic (Ctrl/Cmd + I)' }),
    bulletList: intl.formatMessage({ defaultMessage: 'Unordered List' }),
    numberedList: intl.formatMessage({ defaultMessage: 'Ordered List' }),
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setExpand(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const editor = useEditor({
    extensions,
    onUpdate: ({ editor: currentEditor }) => {
      formik.setFieldValue('notes', currentEditor.getHTML());
    },
  });

  useEffect(() => {
    if (editor) {
      editor.commands.setContent(note?.description ?? '');
    }
  }, [editor, note]);

  // resets editor when resetForm() is called
  useEffect(() => {
    if (editor && !formik.values.notes) {
      editor.commands.setContent('');
    }
  }, [editor, formik.values.notes]);

  if (!editor) return null;

  const isExpanded = expand || !editor.isEmpty || formik.values.id;

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack
        ref={wrapperRef}
        sx={{
          minHeight: '48px',
          maxHeight: '220px',
          borderRadius: '6px',
          background: (theme) => theme.palette.neutralBlackBg[200],
        }}
      >
        <Stack
          direction='row'
          sx={{
            maxHeight: '157px',
            height: '100%',
            width: '100%',
          }}
        >
          <EditorContent
            editor={editor}
            onFocus={() => {
              setExpand(true);
            }}
          />
        </Stack>
        <Stack
          direction='row'
          sx={{
            display: isExpanded ? 'flex' : 'none',
            borderTop: (theme) =>
              `1px solid ${theme.palette.neutralBlackBg[500]}`,
            padding: '8px',
            width: '100%',
          }}
          justifyContent='space-between'
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              flex: 1,
              overflowX: 'auto',
            }}
          >
            <Tooltip title={toolTipMessages.formatting}>
              <ToggleButton
                value='toggleFormatting'
                sx={{
                  border: 0,
                }}
                color='teal'
                selected={formattingEnabled}
                onChange={() => {
                  setFormattingEnabled(!formattingEnabled);
                  editor
                    .chain()
                    .selectAll()
                    .unsetAllMarks()
                    .clearNodes()
                    .focus()
                    .run();
                }}
              >
                <AbcIcon />
              </ToggleButton>
            </Tooltip>
            <Divider
              flexItem
              orientation='vertical'
              sx={{
                my: 1,
                mx: 1,
              }}
            />
            {formattingEnabled && (
              <>
                <Tooltip title={toolTipMessages.bold}>
                  <ToggleButton
                    value='bold'
                    sx={{
                      border: 0,
                      mr: 0.5,
                    }}
                    color='teal'
                    selected={editor.isActive('bold')}
                    onClick={() => {
                      editor.chain().focus().toggleBold().run();
                    }}
                  >
                    <FormatBoldIcon />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title={toolTipMessages.underline}>
                  <ToggleButton
                    value='underline'
                    sx={{
                      border: 0,
                      mr: 0.5,
                    }}
                    color='teal'
                    selected={editor.isActive('underline')}
                    onClick={() => {
                      editor.chain().focus().toggleUnderline().run();
                    }}
                  >
                    <FormatUnderlinedIcon />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title={toolTipMessages.italic}>
                  <ToggleButton
                    value='italic'
                    sx={{
                      border: 0,
                    }}
                    color='teal'
                    selected={editor.isActive('italic')}
                    onClick={() => {
                      editor.chain().focus().toggleItalic().run();
                    }}
                  >
                    <FormatItalicIcon />
                  </ToggleButton>
                </Tooltip>
                <Divider
                  flexItem
                  orientation='vertical'
                  sx={{
                    my: 1,
                    mx: 1,
                  }}
                />
                <Tooltip title={toolTipMessages.bulletList}>
                  <ToggleButton
                    value='bulletList'
                    sx={{
                      border: 0,
                      mr: 0.5,
                    }}
                    color='teal'
                    selected={editor.isActive('bulletList')}
                    onClick={() => {
                      editor.chain().focus().toggleBulletList().run();
                    }}
                  >
                    <FormatListBulletedIcon />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title={toolTipMessages.numberedList}>
                  <ToggleButton
                    value='numberedList'
                    sx={{
                      border: 0,
                    }}
                    color='teal'
                    selected={editor.isActive('orderedList')}
                    onClick={() => {
                      editor.chain().focus().toggleOrderedList().run();
                    }}
                  >
                    <FormatListNumberedIcon />
                  </ToggleButton>
                </Tooltip>
              </>
            )}
          </Box>
          <Stack direction='row' justifyContent='center' alignItems='center'>
            {formik.values.id && (
              <Button
                type='button'
                variant='text'
                sx={{ color: (theme) => theme.palette.deepSalmon.main }}
                onClick={() => {
                  onClickCancel();
                  formik.resetForm();
                }}
                disabled={formik.isSubmitting}
              >
                {intl.formatMessage({
                  defaultMessage: 'Cancel',
                })}
              </Button>
            )}
            <Button
              type='submit'
              variant='text'
              sx={{ color: (theme) => theme.palette.teal.main }}
              disabled={formik.isSubmitting}
            >
              {intl.formatMessage({
                defaultMessage: 'Save',
              })}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
}

export default NotesForm;
